/**
 * Purchasing API (owner: C2): can-make, shopping lists, baskets and plans, live UCP quotes, watches,
 * alerts, duplicate checks and lookups under /api/purchasing/ (backend/purchasing/api.py). Money is
 * integer minor units plus currency: render it with <Money minor currency>. Grams are decimal strings.
 * Query keys start with "purchasing".
 *
 * Other features send a shortfall to purchasing with `createBasket({ name, lines, source })` (or
 * `useCreateBasket()`), then link to `/purchasing/baskets/<id>`.
 */
import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/api/client";
import type { DecimalString, IsoDateTime, ListParams, MinorUnits, Paged, Uuid } from "@/api/types";

// Can I make this? -------------------------------------------------------------------------------------

export type CoverageStatus = "ok" | "short" | "missing";

export interface CoverageLine {
  material_id: Uuid;
  material_name: string;
  required_g: DecimalString;
  available_g: DecimalString;
  shortfall_g: DecimalString;
  status: CoverageStatus;
  lots: Array<{
    lot_id: Uuid;
    code: string;
    balance_g: DecimalString;
    active_fraction: DecimalString;
    neat_g: DecimalString;
  }>;
}

export interface VersionRef {
  id: Uuid;
  formula_id: Uuid;
  code: string;
  name: string;
  label: string;
  status: string;
}

export interface CanMakeResult {
  formula_version: VersionRef;
  batch_g: DecimalString;
  concentration: DecimalString;
  concentrate_g: DecimalString;
  lines: CoverageLine[];
  solvent: CoverageLine | null;
  can_make: boolean;
  status: "ok" | "short";
  short_count: number;
  warnings: string[];
}

export interface CanMakeInput {
  formula_version_id: Uuid;
  /** Finished batch mass in grams. */
  batch_g: DecimalString;
  /** Fragrance share of the batch as a fraction ("0.2" = 20 %); "1" for a concentrate. */
  concentration?: DecimalString;
  solvent_material_id?: Uuid | null;
}

export function canMake(input: CanMakeInput): Promise<CanMakeResult> {
  return apiPost<CanMakeResult>("/purchasing/can-make", input);
}

export function useCanMake() {
  return useMutation({ mutationFn: canMake });
}

// Shopping lists ---------------------------------------------------------------------------------------

export interface ShoppingListInput {
  items: Array<{ formula_version_id: Uuid; batch_g: DecimalString; concentration?: DecimalString }>;
  /** Extra share on top of what the formulas need ("0.1" = 10 %). */
  safety_margin?: DecimalString;
  subtract_stock?: boolean;
  solvent_material_id?: Uuid | null;
  name?: string;
  dry_run?: boolean;
}

export interface ShoppingListLine {
  material_id: Uuid;
  material_name: string;
  required_g: DecimalString;
  with_margin_g: DecimalString;
  available_g: DecimalString;
  note: string;
  neat_g: DecimalString | null;
}

export interface ShoppingListResult {
  preview: {
    items: Array<VersionRef & { batch_g: DecimalString; concentration: DecimalString }>;
    safety_margin: DecimalString;
    subtract_stock: boolean;
    lines: ShoppingListLine[];
    covered: ShoppingListLine[];
    warnings: string[];
  };
  basket: BasketDetail | null;
}

export function useShoppingList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ShoppingListInput) => apiPost<ShoppingListResult>("/purchasing/shopping-list", input),
    onSuccess: (result) => {
      if (result.basket) void queryClient.invalidateQueries({ queryKey: ["purchasing", "baskets"] });
    },
  });
}

// Baskets ----------------------------------------------------------------------------------------------

export type BasketStatus = "draft" | "planned" | "ordered" | "archived";

export interface Acceptable {
  material_ids?: Uuid[];
  /** Accept any listing of a material with the same substance (Evernyl for Veramoss). */
  same_substance?: boolean;
  grades?: string[];
}

export interface BasketLineInput {
  material_id: Uuid;
  neat_g: DecimalString;
  note?: string;
  acceptable?: Acceptable | null;
}

export interface BasketLine {
  id: Uuid;
  material_id: Uuid;
  material_name: string;
  neat_g: DecimalString;
  note: string;
  acceptable: Acceptable;
  substitutes: Array<{ id: Uuid; name: string }>;
  owned_g: DecimalString | null;
}

export interface PlanSummary {
  id: Uuid;
  created_at: IsoDateTime;
  objective: Objective;
  total_minor: MinorUnits | null;
  currency: string;
  parcels: number;
  unmet_count: number;
}

export interface Basket {
  id: Uuid;
  name: string;
  status: BasketStatus;
  source: Record<string, unknown>;
  notes: string;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
  line_count: number;
  latest_plan: PlanSummary | null;
}

export interface BasketDetail extends Basket {
  lines: BasketLine[];
  plan: Plan | null;
  plans: PlanSummary[];
  /** The lines changed since the latest plan. */
  plan_stale: boolean;
}

export type Objective = "landed_cost" | "fewest_parcels";

export interface PlanWarning {
  code: string;
  message: string;
  material_id?: Uuid;
  supplier_id?: Uuid;
}

export interface PlanItem {
  variant_id: Uuid;
  variant_external_id: string;
  product_id: Uuid;
  product_title: string;
  variant_title: string;
  url: string;
  material_id: Uuid;
  material_name: string;
  serves: Array<{ material_id: Uuid; material_name: string }>;
  packs: number;
  pack_g: DecimalString | null;
  pack_unit: string | null;
  dilution_fraction: DecimalString | null;
  solvent_text: string;
  neat_g: DecimalString;
  unit_price_minor: MinorUnits;
  total_minor: MinorUnits;
  listed_price_minor: MinorUnits;
  listed_currency: string;
  dangerous_goods: boolean;
  match_method: string;
  match_status: "proposed" | "accepted" | "";
  other_market: boolean;
}

export interface PlanRule {
  source: "workspace" | "policy";
  currency: string;
  flat_fee_minor: MinorUnits;
  free_threshold_minor: MinorUnits | null;
  dg_fee_minor: MinorUnits;
  duty_fraction: DecimalString;
  brokerage_minor: MinorUnits;
  min_order_minor: MinorUnits;
  max_order_minor: MinorUnits | null;
  ddp: boolean;
  shipping_known: boolean;
  notes: string[];
}

export interface ImportEstimate {
  currency: string;
  duty_minor: MinorUnits;
  gst_hst_minor: MinorUnits;
  pst_minor: MinorUnits;
  brokerage_minor: MinorUnits;
  total_minor: MinorUnits;
  duty_free: boolean;
  tax_free: boolean;
  notes: string[];
}

export interface Nudge {
  variant_id: Uuid;
  product_title: string;
  variant_title: string;
  material_id: Uuid | null;
  material_name: string;
  price_minor: MinorUnits;
  gap_minor: MinorUnits;
  closes_gap: boolean;
  shipping_saved_minor: MinorUnits;
  net_cost_minor: MinorUnits;
  reason: "in_basket" | "watched";
  url: string;
}

export interface LiveQuote {
  supplier: { id: Uuid; slug: string; name: string };
  address: { country: string; region: string; postal_code: string; city: string };
  fetched_at: IsoDateTime;
  quote: {
    currency: string;
    subtotal_minor: MinorUnits | null;
    shipping_minor: MinorUnits | null;
    total_minor: MinorUnits | null;
    options: Array<{ title: string; amount_minor: MinorUnits | null; description: string }>;
    messages: Array<Record<string, unknown>>;
    continue_url: string;
  } | null;
  converted: {
    currency: string;
    subtotal_minor: MinorUnits | null;
    shipping_minor: MinorUnits | null;
    total_minor: MinorUnits | null;
    options: Array<{ title: string; amount_minor: MinorUnits | null }>;
    note: string;
  } | null;
  error: string;
  message: string;
}

export interface SubBasket {
  supplier: { id: Uuid; slug: string; name: string; platform: string; country: string; url: string; ucp: boolean };
  items: PlanItem[];
  subtotal_minor: MinorUnits;
  shipping_minor: MinorUnits;
  dg_minor: MinorUnits;
  duty_minor: MinorUnits;
  brokerage_minor: MinorUnits;
  total_minor: MinorUnits;
  duty_basis: "rule" | "cbsa_estimate";
  free_shipping: boolean;
  free_threshold_minor: MinorUnits | null;
  gap_to_free_minor: MinorUnits | null;
  rule: PlanRule;
  import_estimate: ImportEstimate | null;
  fx_note: string;
  handoff: { urls: string[]; multi_item: boolean; note: string; platform: string };
  live_quote_available: boolean;
  live_quote: LiveQuote | null;
  nudges: Nudge[];
  warnings: PlanWarning[];
}

export interface Duplicate {
  material_id: Uuid;
  material_name: string;
  kind: "owned" | "in_request";
  same_id: boolean;
  severity: "info" | "warning";
  matches: Array<{ material_id: Uuid; material_name: string; owned_g: DecimalString | null }>;
  message: string;
}

export interface PlanResult {
  currency: string;
  objective: Objective;
  solver: "milp" | "greedy";
  lines_hash: string;
  ship_to: { country: string; region: string; postal_code: string };
  total_minor: MinorUnits;
  parcels: number;
  sub_baskets: SubBasket[];
  unmet: Array<{ material_id: Uuid; material_name: string; missing_g: DecimalString; reason: string }>;
  warnings: PlanWarning[];
  duplicates: Duplicate[];
  skipped: Array<{ supplier: { id: Uuid; name: string }; reason: string; listings: number }>;
  offers_considered: number;
  include_proposed: boolean;
}

export interface Plan {
  id: Uuid;
  basket_id: Uuid;
  created_at: IsoDateTime;
  objective: Objective;
  total_minor: MinorUnits | null;
  currency: string;
  result: PlanResult;
}

export interface BasketListParams extends ListParams {
  status?: BasketStatus;
  include_archived?: boolean;
}

export const purchasingKeys = {
  all: ["purchasing"] as const,
  baskets: (params: BasketListParams) => ["purchasing", "baskets", "list", params] as const,
  basket: (id: Uuid) => ["purchasing", "baskets", "detail", id] as const,
  watches: (params: ListParams) => ["purchasing", "watches", params] as const,
  alerts: (params: AlertListParams) => ["purchasing", "alerts", "list", params] as const,
  unread: () => ["purchasing", "alerts", "unread"] as const,
  settings: () => ["purchasing", "settings"] as const,
};

export function useBaskets(params: BasketListParams) {
  return useQuery({
    queryKey: purchasingKeys.baskets(params),
    queryFn: ({ signal }) => apiGet<Paged<Basket>>("/purchasing/baskets/", { params: { ...params }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function basketQuery(id: Uuid) {
  return queryOptions({
    queryKey: purchasingKeys.basket(id),
    queryFn: ({ signal }) => apiGet<BasketDetail>(`/purchasing/baskets/${id}`, { signal }),
  });
}

export function useBasket(id: Uuid) {
  return useQuery(basketQuery(id));
}

/** Create a draft basket (other features call this to send a shortfall to purchasing). */
export function createBasket(input: {
  name: string;
  lines: BasketLineInput[];
  source?: Record<string, unknown>;
  notes?: string;
}): Promise<BasketDetail> {
  return apiPost<BasketDetail>("/purchasing/baskets", input);
}

export function useCreateBasket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createBasket,
    onSuccess: (basket) => {
      queryClient.setQueryData(purchasingKeys.basket(basket.id), basket);
      void queryClient.invalidateQueries({ queryKey: ["purchasing", "baskets", "list"] });
    },
  });
}

function useBasketMutation<TInput>(request: (input: TInput) => Promise<BasketDetail | undefined | void>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (basket) => {
      if (basket) queryClient.setQueryData(purchasingKeys.basket(basket.id), basket);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["purchasing", "baskets"] }),
  });
}

export function useUpdateBasket(id: Uuid) {
  return useBasketMutation((changes: { name?: string; status?: BasketStatus; notes?: string }) =>
    apiPatch<BasketDetail>(`/purchasing/baskets/${id}`, changes),
  );
}

/** Archive, restore or mark ordered any basket (for lists). */
export function useSetBasketStatus() {
  return useBasketMutation(({ id, status }: { id: Uuid; status: BasketStatus }) =>
    apiPatch<BasketDetail>(`/purchasing/baskets/${id}`, { status }),
  );
}

export function useDeleteBasket() {
  return useBasketMutation((id: Uuid) => apiDelete(`/purchasing/baskets/${id}`));
}

export function useAddBasketLine(id: Uuid) {
  return useBasketMutation((line: BasketLineInput) => apiPost<BasketDetail>(`/purchasing/baskets/${id}/lines`, line));
}

export function useUpdateBasketLine(id: Uuid) {
  return useBasketMutation(
    ({ lineId, ...changes }: { lineId: Uuid; neat_g?: DecimalString; note?: string; acceptable?: Acceptable }) =>
      apiPatch<BasketDetail>(`/purchasing/baskets/${id}/lines/${lineId}`, changes),
  );
}

export function useDeleteBasketLine(id: Uuid) {
  return useBasketMutation((lineId: Uuid) => apiDelete<BasketDetail>(`/purchasing/baskets/${id}/lines/${lineId}`));
}

export function usePlanBasket(id: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { objective: Objective; include_proposed?: boolean }) =>
      apiPost<Plan>(`/purchasing/baskets/${id}/plan`, body),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["purchasing", "baskets"] }),
  });
}

export function useLiveQuote(basketId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { supplier_id: Uuid; lines: Array<{ variant_id: Uuid; quantity: number }>; plan_id?: Uuid }) =>
      apiPost<LiveQuote>("/purchasing/quotes/ucp", body),
    onSettled: () => queryClient.invalidateQueries({ queryKey: purchasingKeys.basket(basketId) }),
  });
}

export function checkDuplicates(materialIds: Uuid[]): Promise<Duplicate[]> {
  return apiPost<Duplicate[]>("/purchasing/duplicates", { material_ids: materialIds });
}

// Watches and alerts ----------------------------------------------------------------------------------

export interface Watch {
  id: Uuid;
  material_id: Uuid | null;
  material_name: string | null;
  variant_id: Uuid | null;
  variant_title: string | null;
  product_title: string | null;
  product_url: string | null;
  supplier: { id: Uuid; name: string } | null;
  /** Variant watches: target pack price. */
  target_price_minor: MinorUnits | null;
  /** Material watches: target price per neat gram (decimal minor units). */
  target_per_g_minor: DecimalString | null;
  currency: string;
  notify_back_in_stock: boolean;
  active: boolean;
  created_at: IsoDateTime;
  current: {
    price_minor: MinorUnits | null;
    currency: string;
    per_neat_g_minor: DecimalString | null;
    in_stock: boolean | null;
    supplier_name?: string;
    product_title?: string;
    variant_title?: string;
  } | null;
}

export interface WatchInput {
  material_id?: Uuid | null;
  variant_id?: Uuid | null;
  target_price_minor?: MinorUnits | null;
  target_per_g_minor?: DecimalString | null;
  notify_back_in_stock?: boolean;
  active?: boolean;
}

export function useWatches(params: ListParams = {}) {
  return useQuery({
    queryKey: purchasingKeys.watches(params),
    queryFn: ({ signal }) =>
      apiGet<Paged<Watch>>("/purchasing/watches/", { params: { limit: 200, ...params }, signal }),
  });
}

export function useCreateWatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: WatchInput) => apiPost<Watch>("/purchasing/watches/", input),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["purchasing", "watches"] }),
  });
}

export function useUpdateWatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...changes }: WatchInput & { id: Uuid }) =>
      apiPatch<Watch>(`/purchasing/watches/${id}`, changes),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["purchasing", "watches"] }),
  });
}

export function useDeleteWatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => apiDelete(`/purchasing/watches/${id}`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["purchasing", "watches"] }),
  });
}

export type AlertKind =
  "price_drop" | "back_in_stock" | "new_listing" | "out_of_stock" | "duplicate_purchase" | "supplier_inactive";

export interface Alert {
  id: Uuid;
  kind: AlertKind;
  payload: {
    title?: string;
    supplier?: { id: Uuid | null; slug: string; name: string };
    product?: { id: Uuid; title: string; url: string };
    variant?: { id: Uuid; title: string };
    material?: { id: Uuid; name: string };
    [key: string]: unknown;
  };
  read: boolean;
  created_at: IsoDateTime;
  watch_id: Uuid | null;
  title: string;
  detail: string;
}

export interface AlertListParams extends ListParams {
  unread?: boolean;
  kind?: AlertKind;
}

export function useAlerts(params: AlertListParams) {
  return useQuery({
    queryKey: purchasingKeys.alerts(params),
    queryFn: ({ signal }) => apiGet<Paged<Alert>>("/purchasing/alerts/", { params: { ...params }, signal }),
    placeholderData: keepPreviousData,
  });
}

/** Unread alerts (the dashboard and navigation can show this count). */
export function useUnreadAlertCount() {
  return useQuery({
    queryKey: purchasingKeys.unread(),
    queryFn: ({ signal }) => apiGet<{ count: number }>("/purchasing/alerts/unread-count", { signal }),
    staleTime: 60_000,
  });
}

export function useMarkAlertsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids?: Uuid[]; all?: boolean; read?: boolean }) =>
      apiPost<{ count: number }>("/purchasing/alerts/read", body),
    onMutate: async ({ ids, all, read = true }) => {
      await queryClient.cancelQueries({ queryKey: ["purchasing", "alerts"] });
      const snapshots = queryClient.getQueriesData<Paged<Alert>>({ queryKey: ["purchasing", "alerts", "list"] });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        queryClient.setQueryData<Paged<Alert>>(key, {
          ...data,
          items: data.items.map((alert) => (all || ids?.includes(alert.id) ? { ...alert, read } : alert)),
        });
      }
      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      for (const [key, data] of context?.snapshots ?? []) queryClient.setQueryData(key, data);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["purchasing", "alerts"] }),
  });
}

// Lookups and settings --------------------------------------------------------------------------------

export interface MaterialOption {
  id: Uuid;
  name: string;
  trade_name: string;
  kind: string;
  cas: string | null;
  owned_g: DecimalString | null;
  listing_count: number;
}

export function searchMaterials(q: string, signal?: AbortSignal): Promise<MaterialOption[]> {
  return apiGet<MaterialOption[]>("/purchasing/materials", { params: { q, limit: 20 }, signal });
}

export interface FormulaVersionOption {
  id: Uuid;
  formula_id: Uuid;
  code: string;
  name: string;
  label: string;
  status: string;
  number: number;
}

export function searchFormulaVersions(q: string, signal?: AbortSignal): Promise<FormulaVersionOption[]> {
  return apiGet<FormulaVersionOption[]>("/purchasing/formula-versions", { params: { q, limit: 30 }, signal });
}

export interface PurchasingSettings {
  allow_ucp_quotes: boolean;
  continuity_stale_days: number;
  default_safety_margin: DecimalString;
}

export function usePurchasingSettings() {
  return useQuery({
    queryKey: purchasingKeys.settings(),
    queryFn: ({ signal }) => apiGet<PurchasingSettings>("/purchasing/settings", { signal }),
  });
}

export function useUpdatePurchasingSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (changes: Partial<PurchasingSettings>) => apiPatch<PurchasingSettings>("/purchasing/settings", changes),
    onSuccess: (settings) => queryClient.setQueryData(purchasingKeys.settings(), settings),
  });
}
